
import { useEffect, useMemo, useState } from 'react';
import {
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Clock3,
  FileText,
  MapPin,
  Play,
  UserRound,
  Video,
  XCircle,
} from 'lucide-react';
import type { Interview } from '@/modules/hrPortal/types/interview.types';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Textarea } from '@/shared/components/ui/textarea';

interface StatusHistoryItem {
  status?: string;
  date?: string;
  time?: string;
  reason?: string;
  remarks?: string;
  action?: string;
  performedBy?: string | null;
}

/**
 * Interview information stored in Master Database.
 * Used only for manually added and Excel/CSV imported records.
 */
export interface MasterDatabaseInterviewData {
  interviewLineup?: string | null;
  interviewLinedupDate?: string | null;
  hrInterviewDate?: string | null;
  hrInterviewName?: string | null;
  hrInterviewStatus?: string | null;
  business1Date?: string | null;
  business1InterviewerName?: string | null;
}

interface ApplicationInterviewPanelProps {
  statusHistory: StatusHistoryItem[];
  interviews: Interview[];
  interviewsLoading?: boolean;
  interviewsError?: string | null;
  canAccessInterviewScheduling?: boolean;
  isMasterDatabaseRecord?: boolean;
  masterDatabaseInterviewData?: MasterDatabaseInterviewData | null;

  interviewWorkflow: {
    key: string;
    name: string;
    sequence: number;
  }[];

  onStartInterview?: (interviewId: string) => Promise<void>;
  onCancelInterview?: (interviewId: string, reason: string) => Promise<void>;
  onNoShowInterview?: (interviewId: string, reason: string) => Promise<void>;
  onCompleteInterview?: (
    interviewId: string,
    result: 'PASSED' | 'FAILED',
    remarks?: string
  ) => Promise<void>;
  onOpenReschedule?: (interview: Interview) => void;
}

type ActiveItem =
  | {
      type: 'status' | 'interview';
      key: string;
    }
  | null;

type ActionDialog = 'cancel' | 'no-show' | 'complete' | null;

const statusLabels: Record<string, string> = {
  COMPLETED: 'Completed',
  SCHEDULED: 'Scheduled',
  RESCHEDULED: 'Rescheduled',
  IN_PROGRESS: 'In Progress',
  CANCELLED: 'Cancelled',
  NO_SHOW: 'No Show',
};

const statusClasses: Record<string, string> = {
  COMPLETED: 'bg-emerald-50 text-emerald-600',
  SCHEDULED: 'bg-blue-50 text-blue-600',
  RESCHEDULED: 'bg-blue-50 text-blue-600',
  IN_PROGRESS: 'bg-amber-50 text-amber-600',
  CANCELLED: 'bg-red-50 text-red-600',
  NO_SHOW: 'bg-red-50 text-red-600',
};

const iconClasses: Record<string, string> = {
  COMPLETED: 'bg-emerald-500 text-white',
  SCHEDULED: 'bg-blue-500 text-white',
  RESCHEDULED: 'bg-blue-500 text-white',
  IN_PROGRESS: 'bg-amber-500 text-white',
  CANCELLED: 'bg-red-500 text-white',
  NO_SHOW: 'bg-red-500 text-white',
};

const formatStatusDateTime = (date?: string, time?: string) => {
  if (!date && !time) return '';
  if (date && time) return `${date} at ${time}`;
  return date || time || '';
};

const formatInterviewDate = (date?: string) => {
  if (!date) return 'To be scheduled';

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return date;

  return parsedDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getInterviewModeLabel = (interview: Interview) => {
  if (interview.mode === 'ONLINE') {
    return interview.meetingPlatform
      ? `Online (${interview.meetingPlatform})`
      : 'Online';
  }

  if (interview.mode === 'OFFLINE') return 'Offline';

  return 'Phone';
};

const getInterviewStatusLabel = (interview?: Interview) =>
  interview
    ? statusLabels[interview.status] || interview.status
    : 'Not Scheduled';

const getInterviewStatusClass = (interview?: Interview) =>
  interview
    ? statusClasses[interview.status] || 'bg-slate-100 text-slate-500'
    : 'bg-slate-100 text-slate-500';

const getInterviewIconClass = (interview?: Interview) =>
  interview
    ? iconClasses[interview.status] || 'bg-slate-500 text-white'
    : 'bg-slate-100 text-slate-500';


export default function ApplicationInterviewPanel({
  statusHistory,
  interviews,
  interviewsLoading = false,
  interviewsError = null,
  interviewWorkflow,
  onStartInterview,
  onCancelInterview,
  onNoShowInterview,
  onCompleteInterview,
  onOpenReschedule,
  canAccessInterviewScheduling = true,
  isMasterDatabaseRecord = false,
  masterDatabaseInterviewData = null,
}: ApplicationInterviewPanelProps) {
  const [activeTab, setActiveTab] = useState<
    'application' | 'interview'
  >('application');

  const [activeItem, setActiveItem] = useState<ActiveItem>(null);
  const [actionDialog, setActionDialog] = useState<ActionDialog>(null);
  const [selectedInterview, setSelectedInterview] =
    useState<Interview | null>(null);

  const [actionReason, setActionReason] = useState('');
  const [completeResult, setCompleteResult] =
    useState<'PASSED' | 'FAILED'>('PASSED');

  const [completeRemarks, setCompleteRemarks] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (canAccessInterviewScheduling || isMasterDatabaseRecord) return;

    setActiveTab('application');

    setActiveItem((current) =>
      current?.type === 'interview' ? null : current
    );

    setActionDialog(null);
    setSelectedInterview(null);
    setActionReason('');
    setCompleteRemarks('');
    setActionError(null);
  }, [canAccessInterviewScheduling, isMasterDatabaseRecord]);

  const canShowInterviewTab = canAccessInterviewScheduling;

  const visibleActiveTab = canShowInterviewTab
    ? activeTab
    : 'application';

  const sortedWorkflow = useMemo(
    () => [...interviewWorkflow].sort(
      (a, b) => a.sequence - b.sequence
    ),
    [interviewWorkflow]
  );

  const masterDatabaseTimeline = [
    {
      key: 'interviewLineup',
      label: 'Interview Lineup',
      value: masterDatabaseInterviewData?.interviewLineup,
    },
    {
      key: 'interviewLinedupDate',
      label: 'Interview Linedup Date',
      value: masterDatabaseInterviewData?.interviewLinedupDate
        ? formatInterviewDate(masterDatabaseInterviewData.interviewLinedupDate)
        : '',
    },
    {
      key: 'hrInterviewDate',
      label: 'HR Interview Date',
      value: masterDatabaseInterviewData?.hrInterviewDate
        ? formatInterviewDate(masterDatabaseInterviewData.hrInterviewDate)
        : '',
    },
    {
      key: 'hrInterviewName',
      label: 'HR Interview Name',
      value: masterDatabaseInterviewData?.hrInterviewName,
    },
    {
      key: 'hrInterviewStatus',
      label: 'HR Interview Status',
      value: masterDatabaseInterviewData?.hrInterviewStatus,
    },
    {
      key: 'business1Date',
      label: 'Business-1 Date',
      value: masterDatabaseInterviewData?.business1Date
        ? formatInterviewDate(masterDatabaseInterviewData.business1Date)
        : '',
    },
    {
      key: 'business1InterviewerName',
      label: 'Business-1 Interviewer Name',
      value: masterDatabaseInterviewData?.business1InterviewerName,
    },
  ].map((item) => ({
    ...item,
    displayValue: String(item.value ?? '').trim(),
  }));

  const interviewsByRound = useMemo(() => {
    const result: Record<string, Interview | undefined> = {};

    sortedWorkflow.forEach((round) => {
      result[round.key] = interviews
        .filter((interview) => interview.round === round.key)
        .sort((a, b) => {
          const first = new Date(
            `${a.date}T${a.time || '00:00'}`
          ).getTime();

          const second = new Date(
            `${b.date}T${b.time || '00:00'}`
          ).getTime();

          return second - first;
        })[0];
    });

    return result;
  }, [interviews, sortedWorkflow]);

  const toggleItem = (
    type: 'status' | 'interview',
    key: string
  ) => {
    setActiveItem((current) =>
      current?.type === type && current.key === key
        ? null
        : { type, key }
    );
  };

  const openActionDialog = (
    type: ActionDialog,
    interview: Interview
  ) => {
    if (!canAccessInterviewScheduling) return;

    setSelectedInterview(interview);
    setActionDialog(type);
    setActionReason('');
    setCompleteRemarks('');
    setCompleteResult('PASSED');
    setActionError(null);
  };

  const closeActionDialog = () => {
    if (actionLoading) return;

    setActionDialog(null);
    setSelectedInterview(null);
    setActionReason('');
    setCompleteRemarks('');
    setActionError(null);
  };

  const handleStart = async (interview: Interview) => {
    if (!canAccessInterviewScheduling || !onStartInterview) return;

    try {
      setActionError(null);
      setActionLoading(true);

      await onStartInterview(interview.id);
    } catch (error: any) {
      setActionError(
        typeof error === 'string'
          ? error
          : error?.message || 'Unable to start interview.'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleAction = async () => {
    if (!canAccessInterviewScheduling || !selectedInterview) return;

    const reason = actionReason.trim();
    const remarks = completeRemarks.trim();

    if (
      (actionDialog === 'cancel' || actionDialog === 'no-show') &&
      !reason
    ) {
      setActionError(
        actionDialog === 'cancel'
          ? 'Cancellation reason is required.'
          : 'No-show reason is required.'
      );

      return;
    }

    try {
      setActionError(null);
      setActionLoading(true);

      if (actionDialog === 'cancel' && onCancelInterview) {
        await onCancelInterview(selectedInterview.id, reason);
      }

      if (actionDialog === 'no-show' && onNoShowInterview) {
        await onNoShowInterview(selectedInterview.id, reason);
      }

      if (actionDialog === 'complete' && onCompleteInterview) {
        await onCompleteInterview(
          selectedInterview.id,
          completeResult,
          remarks || undefined
        );
      }

      closeActionDialog();
    } catch (error: any) {
      setActionError(
        typeof error === 'string'
          ? error
          : error?.message || 'Unable to update interview.'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const renderStatusDetails = (item: StatusHistoryItem) => (
    <div className="mb-2 ml-8 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
      <div className="space-y-2 text-xs">
        {[
          ['Status', item.status || '—'],
          ['Date', item.date || '—'],
          ['Time', item.time || '—'],
          ['Changed By', item.performedBy],
          ['Action', item.action],
        ].map(
          ([label, value]) =>
            value && (
              <div
                key={label}
                className="flex justify-between gap-3"
              >
                <span className="text-slate-500">{label}</span>
                <span className="font-medium text-slate-800">
                  {value}
                </span>
              </div>
            )
        )}

        {item.reason && (
          <div>
            <p className="text-slate-500">Reason</p>
            <p className="mt-0.5 text-slate-800">{item.reason}</p>
          </div>
        )}

        {item.remarks && (
          <div>
            <p className="text-slate-500">Remarks</p>
            <p className="mt-0.5 text-slate-800">{item.remarks}</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderInterviewDetails = (interview: Interview) => (
    <div className="space-y-3 text-xs">
      <div className="space-y-2.5">
        <div className="flex items-start gap-2">
          <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />

          <div>
            <p className="text-slate-500">Date</p>
            <p className="font-medium text-slate-800">
              {formatInterviewDate(interview.date)}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />

          <div>
            <p className="text-slate-500">Time & Duration</p>

            <p className="font-medium text-slate-800">
              {interview.time || '—'}
              {interview.duration
                ? ` · ${interview.duration} minutes`
                : ''}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />

          <div>
            <p className="text-slate-500">Interviewer</p>

            <p className="font-medium text-slate-800">
              {interview.interviewer?.name ||
                interview.interviewerId ||
                '—'}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <Video className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />

          <div>
            <p className="text-slate-500">Mode</p>

            <p className="font-medium text-slate-800">
              {getInterviewModeLabel(interview)}
            </p>
          </div>
        </div>

        {interview.mode === 'ONLINE' && interview.meetingLink && (
          <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2">
            <p className="text-slate-500">Meeting Link</p>

            <a
              href={interview.meetingLink}
              target="_blank"
              rel="noreferrer"
              className="mt-0.5 block truncate font-medium text-blue-600 hover:underline"
            >
              {interview.meetingLink}
            </a>
          </div>
        )}

        {interview.mode === 'OFFLINE' && (
          <div className="space-y-2">
            {interview.venue && (
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />

                <div>
                  <p className="text-slate-500">Venue</p>
                  <p className="font-medium text-slate-800">
                    {interview.venue}
                  </p>
                </div>
              </div>
            )}

            {interview.address && (
              <div>
                <p className="text-slate-500">Address</p>
                <p className="mt-0.5 font-medium text-slate-800">
                  {interview.address}
                </p>
              </div>
            )}

            {interview.googleMapsLocation && (
              <a
                href={interview.googleMapsLocation}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 hover:underline"
              >
                <MapPin className="h-3.5 w-3.5" />
                View location
              </a>
            )}
          </div>
        )}

        {interview.result && (
          <div className="flex justify-between gap-3">
            <span className="text-slate-500">Result</span>

            <span
              className={`font-semibold ${
                interview.result === 'PASSED'
                  ? 'text-emerald-600'
                  : 'text-red-600'
              }`}
            >
              {interview.result}
            </span>
          </div>
        )}

        {interview.instructions && (
          <div>
            <p className="text-slate-500">Instructions</p>
            <p className="mt-0.5 text-slate-800">
              {interview.instructions}
            </p>
          </div>
        )}

        {interview.remarks && (
          <div>
            <p className="text-slate-500">Remarks</p>
            <p className="mt-0.5 text-slate-800">
              {interview.remarks}
            </p>
          </div>
        )}
      </div>

      {['SCHEDULED', 'RESCHEDULED'].includes(interview.status) && (
        <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-3">
          <Button
            type="button"
            size="sm"
            disabled={actionLoading || !onStartInterview}
            onClick={() => handleStart(interview)}
            className="gap-1.5"
          >
            <Play className="h-3.5 w-3.5" />
            Start
          </Button>

          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={actionLoading || !onOpenReschedule}
            onClick={() => onOpenReschedule?.(interview)}
            className="gap-1.5"
          >
            <CalendarDays className="h-3.5 w-3.5" />
            Reschedule
          </Button>

          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={actionLoading}
            onClick={() => openActionDialog('cancel', interview)}
            className="gap-1.5"
          >
            <XCircle className="h-3.5 w-3.5" />
            Cancel
          </Button>
        </div>
      )}

      {['CANCELLED', 'NO_SHOW'].includes(interview.status) && (
        <div className="border-t border-slate-200 pt-3">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={actionLoading || !onOpenReschedule}
            onClick={() => onOpenReschedule?.(interview)}
            className="gap-1.5"
          >
            <CalendarDays className="h-3.5 w-3.5" />
            Reschedule
          </Button>
        </div>
      )}

      {interview.status === 'IN_PROGRESS' && (
        <div className="border-t border-slate-200 pt-3">
          <Button
            type="button"
            size="sm"
            disabled={actionLoading || !onCompleteInterview}
            onClick={() => openActionDialog('complete', interview)}
            className="gap-1.5"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Complete Interview
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div
          className={`grid ${
            canShowInterviewTab ? 'grid-cols-2' : 'grid-cols-1'
          } border-b border-slate-200`}
        >
          {[
            {
              key: 'application' as const,
              icon: FileText,
              label: 'Application Status',
            },
            {
              key: 'interview' as const,
              icon: CalendarDays,
              label: 'Interview Rounds',
            },
          ]
            .filter(
              ({ key }) =>
                key === 'application' || canShowInterviewTab
            )
            .map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setActiveTab(key);
                  setActiveItem(null);
                }}
                className={`flex items-center justify-center gap-2 border-b-2 px-3 py-3 text-sm font-semibold transition-colors ${
                  visibleActiveTab === key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
        </div>

        <div className="h-[390px] overflow-y-auto px-3 py-3 [scrollbar-color:#cbd5e1_transparent] [scrollbar-width:thin]">
          {visibleActiveTab === 'application' ? (
            <div className="relative pl-1">
              <div
                className={`absolute bottom-5 left-[18px] top-5 w-px ${
                  isMasterDatabaseRecord ? 'bg-slate-200' : 'bg-blue-200'
                }`}
              />

              <div className="space-y-1">
                {statusHistory.map((item, index) => {
                  const key = `status-${index}`;

                  const isOpen =
                    activeItem?.type === 'status' &&
                    activeItem.key === key;

                  return (
                    <div
                      key={`${item.status}-${index}`}
                      className="relative"
                    >
                      <button
                        type="button"
                        onClick={() => toggleItem('status', key)}
                        className="relative flex w-full items-start gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-slate-50"
                      >
                        <span
                          className={`relative z-10 mt-1.5 h-3 w-3 shrink-0 rounded-full ring-4 ring-white ${
                            index === statusHistory.length - 1
                              ? 'bg-emerald-500'
                              : 'bg-blue-500'
                          }`}
                        />

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-slate-800">
                              {item.status || 'Applied'}
                            </p>

                            <ChevronRight
                              className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${
                                isOpen ? 'rotate-90' : ''
                              }`}
                            />
                          </div>

                          <p className="mt-0.5 text-xs text-slate-500">
                            {formatStatusDateTime(item.date, item.time)}
                          </p>
                        </div>
                      </button>

                      {isOpen && renderStatusDetails(item)}
                    </div>
                  );
                })}

                {isMasterDatabaseRecord &&
                  masterDatabaseTimeline.map((item) => {
                    const hasValue = Boolean(item.displayValue);

                    return (
                      <div
                        key={item.key}
                        className="relative flex w-full items-start gap-3 rounded-lg px-2 py-2 text-left"
                      >
                        <span
                          className={`relative z-10 mt-1.5 h-3 w-3 shrink-0 rounded-full ring-4 ring-white ${
                            hasValue ? 'bg-emerald-500' : 'bg-slate-300'
                          }`}
                        />

                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-800">
                            {item.label}
                          </p>
                          <p
                            className={`mt-0.5 text-xs ${
                              hasValue ? 'text-slate-500' : 'text-slate-400'
                            }`}
                          >
                            {item.displayValue || 'To be updated'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          ) : (
            /*
             * PORTAL APPLICATION RECORDS
             *
             * Existing Interview API workflow remains unchanged.
             */
            <div className="space-y-2">
              {interviewsLoading ? (
                <div className="flex h-48 items-center justify-center">
                  <p className="text-sm text-slate-500">
                    Loading interviews...
                  </p>
                </div>
              ) : interviewsError ? (
                <div className="flex h-48 items-center justify-center px-4 text-center">
                  <p className="text-sm text-red-500">
                    {interviewsError}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sortedWorkflow.map((round) => {
                    const interview = interviewsByRound[round.key];
                    const key = `interview-${round.key}`;

                    const isOpen =
                      activeItem?.type === 'interview' &&
                      activeItem.key === key;

                    return (
                      <div
                        key={round.key}
                        className="rounded-lg border border-slate-200 bg-white"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            toggleItem('interview', key)
                          }
                          className="flex w-full items-center gap-3 px-3 py-3 text-left"
                        >
                          <div
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${getInterviewIconClass(
                              interview
                            )}`}
                          >
                            {interview ? (
                              <CalendarDays className="h-4 w-4" />
                            ) : (
                              <Clock3 className="h-4 w-4" />
                            )}
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-slate-800">
                                {round.name}
                              </p>

                              <span
                                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${getInterviewStatusClass(
                                  interview
                                )}`}
                              >
                                {getInterviewStatusLabel(interview)}
                              </span>
                            </div>

                            {interview ? (
                              <div className="mt-1 space-y-0.5 text-xs text-slate-500">
                                <p>
                                  {formatInterviewDate(interview.date)}
                                  {interview.time
                                    ? `, ${interview.time}`
                                    : ''}
                                </p>

                                {interview.interviewer?.name && (
                                  <p>{interview.interviewer.name}</p>
                                )}

                                <p>{getInterviewModeLabel(interview)}</p>
                              </div>
                            ) : (
                              <p className="mt-1 text-xs text-slate-400">
                                To be scheduled
                              </p>
                            )}
                          </div>

                          <ChevronRight
                            className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${
                              isOpen ? 'rotate-90' : ''
                            }`}
                          />
                        </button>

                        {isOpen && (
                          <div className="border-t border-slate-200 bg-slate-50 px-3 py-3">
                            {interview ? (
                              renderInterviewDetails(interview)
                            ) : (
                              <p className="text-xs text-slate-500">
                                This interview round has not been scheduled yet.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={canAccessInterviewScheduling && actionDialog !== null}
        onOpenChange={(open) => {
          if (!open) closeActionDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog === 'cancel' && 'Cancel Interview'}
              {actionDialog === 'no-show' && 'Mark as No-Show'}
              {actionDialog === 'complete' && 'Complete Interview'}
            </DialogTitle>
          </DialogHeader>

          {actionDialog === 'complete' ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">
                  Interview Result
                </p>

                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={
                      completeResult === 'PASSED'
                        ? 'default'
                        : 'outline'
                    }
                    disabled={actionLoading}
                    onClick={() => setCompleteResult('PASSED')}
                  >
                    Passed
                  </Button>

                  <Button
                    type="button"
                    variant={
                      completeResult === 'FAILED'
                        ? 'default'
                        : 'outline'
                    }
                    disabled={actionLoading}
                    onClick={() => setCompleteResult('FAILED')}
                  >
                    Failed
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="remarks"
                  className="text-sm font-medium text-slate-700"
                >
                  Remarks (Optional)
                </label>

                <Textarea
                  id="remarks"
                  value={completeRemarks}
                  onChange={(e) =>
                    setCompleteRemarks(e.target.value)
                  }
                  placeholder="Enter any interview feedback or remarks..."
                  disabled={actionLoading}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <label
                htmlFor="reason"
                className="text-sm font-medium text-slate-700"
              >
                Reason <span className="text-red-500">*</span>
              </label>

              <Textarea
                id="reason"
                value={actionReason}
                onChange={(e) =>
                  setActionReason(e.target.value)
                }
                placeholder={`Enter reason for ${
                  actionDialog === 'cancel'
                    ? 'cancellation'
                    : 'no-show'
                }...`}
                disabled={actionLoading}
              />
            </div>
          )}

          {actionError && (
            <p className="text-sm font-medium text-red-500">
              {actionError}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={actionLoading}
              onClick={closeActionDialog}
            >
              Cancel
            </Button>

            <Button
              type="button"
              disabled={actionLoading}
              onClick={handleAction}
            >
              {actionLoading ? 'Saving...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
